import { useTranslation } from 'react-i18next';

import { ClampedText } from '@/components/shared/clamped-text';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatNotificationDate } from '@/utils/date-time';

import {
    getNotificationVisual,
    TONE_ACCENT_BAR_CLASSES,
    TONE_ICON_CLASSES,
    TONE_ROW_UNREAD_CLASSES,
} from '../notification-visuals';
import type { INotification } from '../types';
import { NotificationIcon } from './NotificationIcon';

interface NotificationRowProps {
    notification: INotification;
    onMarkRead: (id: string) => void;
    onNavigate: (notification: INotification) => void;
}

/**
 * One notification, as a full-bleed list row: a per-event colored icon chip
 * (see `notification-visuals`) on the left, the title and its timestamp facing
 * each other on one line, the body beneath. Rows are separated by the list's
 * own dividers rather than each being its own card.
 *
 * Unread carries a left accent bar plus a tonal tint and a semibold title.
 *
 * The timestamp and "mark as read" share one slot on the right: showing both
 * would either wrap the header or cover the time, so the time steps aside on
 * hover and the action takes its place. That keeps the row height fixed, so a
 * list does not jump as the pointer travels down it.
 */
export function NotificationRow({ notification, onMarkRead, onNavigate }: NotificationRowProps) {
    const { t } = useTranslation();
    const { id, title, body, createdAt, isRead, actionUrl, category, metadata } = notification;
    const { tone } = getNotificationVisual(category, metadata);

    return (
        <div
            className={cn(
                'group relative flex gap-3 px-3 py-3 transition-colors',
                actionUrl && 'cursor-pointer',
                isRead ? 'hover:bg-muted/40' : TONE_ROW_UNREAD_CLASSES[tone],
            )}
            onClick={() => onNavigate(notification)}
        >
            {!isRead && (
                <span
                    aria-hidden
                    className={cn(
                        'absolute inset-y-0 left-0 w-0.75',
                        TONE_ACCENT_BAR_CLASSES[tone],
                    )}
                />
            )}

            <div
                className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-lg border',
                    TONE_ICON_CLASSES[tone],
                )}
            >
                <NotificationIcon category={category} metadata={metadata} className="size-5" />
            </div>

            <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                    <p
                        className={cn(
                            'min-w-0 flex-1 truncate text-sm text-foreground',
                            isRead ? 'font-medium' : 'font-semibold',
                        )}
                    >
                        {title}
                    </p>

                    <div className="relative flex shrink-0 items-center">
                        <span
                            className={cn(
                                'text-xs text-muted-foreground transition-opacity',
                                !isRead && 'group-hover:opacity-0',
                            )}
                        >
                            {formatNotificationDate(createdAt)}
                        </span>
                        {!isRead && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon-xs"
                                        className="absolute -top-1 right-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onMarkRead(id);
                                        }}
                                    >
                                        <NotificationIcon name="Check" className="size-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {t('notifications.markAsRead', 'Mark as read')}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                </div>

                <ClampedText
                    text={body}
                    lines={2}
                    className="mt-0.5 text-[13px] leading-snug text-muted-foreground"
                />
            </div>
        </div>
    );
}
